import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  height: 100%;
`;

export const ResetHeader = styled.div`
  background: var(--surface);
  padding: 120px 0 60px;
  text-align: center;

  animation: ${fadeInUp} 1s ease-out;

  .reset-icon {
    font-size: 64px;
    color: var(--accent);
    margin-bottom: 20px;
  }

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.8;
  }
`;

export const ResetContent = styled.div`
  padding: 60px 40px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 50vh;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

export const ResetContainer = styled.div`
  background: var(--surface);
  padding: 40px;
  border-radius: 25px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  width: 100%;
  max-width: 500px;
  animation: ${slideInLeft} 1s ease-out;
  transition: all 0.3s ease;
  /* 
  &:hover {
    border-color: var(--accent);
  } */

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

export const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  gap: 15px;
`;

export const Step = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) =>
    props.$active
      ? "var(--accent)"
      : props.$completed
      ? "var(--success)"
      : "var(--accent-light)"};
  color: ${(props) =>
    props.$active || props.$completed ? "var(--surface)" : "var(--accent)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  color: var(--text-primary);
`;

export const FormGroup = styled.div`
  margin-bottom: 25px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .required {
      color: var(--accent);
    }
  }
`;
export const PasswordGroup = styled.div`
  position: relative;
  width: 100%;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent);
  }
`;
export const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-size: 16px;
  font-family: "Source Code Pro", monospace;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &.error {
    border-color: var(--error);
  }

  &:disabled {
    opacity: 0.65;
  }
`;

export const ErrorMessage = styled.div`
  color: var(--error);
  font-size: 12px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;

  .icon {
    font-size: 14px;
  }
`;

export const SuccessMessage = styled.div`
  background: var(--success);
  color: var(--surface);
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: ${fadeInUp} 0.3s ease;

  .icon {
    font-size: 18px;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 18px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  box-shadow: var(--shadow-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 1px;
  margin-bottom: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const BackLink = styled.button`
  width: 100%;
  background: transparent;
  color: var(--accent);
  border: 2px solid var(--accent-light);
  padding: 15px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 1px;
  /* 
  &:hover {
    border-color: var(--accent);
    background: var(--gradient-primary);
    color: var(--surface);
  } */
`;

export const InfoBox = styled.div`
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 25px;
  text-align: center;
  transition: all 0.3s ease;

  /* &:hover {
    border-color: var(--accent);
    background: rgba(124, 14, 51, 0.02);
  } */

  .info-icon {
    font-size: 32px;
    color: var(--accent);
    margin-bottom: 15px;
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.5;
    margin: 0;
  }
`;

export const TokenInput = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 20px 0;

  input {
    width: 50px;
    height: 50px;
    text-align: center;
    border: 2px solid var(--accent-light);
    border-radius: 10px;
    font-size: 18px;
    font-weight: 600;
    font-family: "Source Code Pro", monospace;
    background: var(--surface);
    color: var(--text-primary);
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
    }

    &.filled {
      background: var(--accent);
      color: var(--surface);
      border-color: var(--accent);
    }

    &.error {
      border-color: var(--error);
      background: rgba(255, 0, 0, 0.1);
    }
  }
`;
